import type { Doc } from "../../docs";

/**
 * "Search the help": the panel's question box where an environment has no
 * Ask configured, and its fall-back when the model does not answer (not
 * switched on, busy, the day's cap used) — a search of the docs shelf the
 * portal already carries, pointing at the page and the heading. Not an
 * assistant, so the panel never words it as one.
 *
 * Plain word matching, scored: a word in a page's title counts most, in a
 * heading next, in the text under it least; a section must hold at least
 * half the question's words (not its stop words) to be offered, and a word
 * matches its plural or singular — "passkeys" finds "passkey".
 */
export interface ShelfHit {
  slug: string;
  title: string;
  heading?: { text: string; id: string };
  /** The first sentence under the heading that holds a word of the question, for the reader to judge the hit. */
  snippet: string;
  score: number;
}

const STOP = new Set("a an and are as at be by can do does for from get go how i if in into is it me my of on or so the then there this to up was we what when where which who why will with you your".split(" "));

export function wordsOf(q: string): string[] {
  return [...new Set(q.toLowerCase().replace(/[^a-z0-9\s-]/g, " ").split(/\s+/).filter((w) => w.length > 1 && !STOP.has(w)))];
}

/** The same heading ids the pages render (docs/index.ts slugify; the release notes' own `v<version>`). */
function anchor(doc: Doc, level: number, text: string, slugify: (t: string) => string): string | undefined {
  if (doc.slug !== "release-notes") return slugify(text);
  if (level !== 2) return undefined;
  const name = text.split(/\s+[—–-]\s+/)[0]!.trim();
  return name === "Unreleased" ? "unreleased" : `v${name}`;
}

/** A page as its sections: the part before the first heading, then each `##`/`###` and the text under it. */
function sectionsOf(doc: Doc, slugify: (t: string) => string): Array<{ heading?: { text: string; id: string }; text: string }> {
  const out: Array<{ heading?: { text: string; id: string }; text: string }> = [{ text: "" }];
  let lastAnchored: { text: string; id: string } | undefined;
  for (const line of doc.markdown.replace(/^\s*# .*\n/, "").split("\n")) {
    const m = /^(#{2,3}) (.+)$/.exec(line);
    if (m) {
      const text = m[2]!.trim();
      const id = anchor(doc, m[1]!.length, text, slugify);
      // A heading the page does not anchor (a release's "### Feature") stays under the one above it.
      if (id) lastAnchored = { text, id };
      out.push({ ...(lastAnchored ? { heading: lastAnchored } : {}), text: id ? "" : `${text}\n` });
      continue;
    }
    out[out.length - 1]!.text += `${line}\n`;
  }
  return out;
}

function snippetOf(text: string, words: readonly string[]): string {
  const plain = text
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_>#|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const sentences = plain.split(/(?<=[.!?])\s+/);
  const hit = sentences.find((s) => words.some((w) => s.toLowerCase().includes(w))) ?? sentences[0] ?? "";
  return hit.length > 220 ? `${hit.slice(0, 217).trimEnd()}…` : hit;
}

export function searchShelf(docs: readonly Doc[], question: string, slugify: (t: string) => string, limit = 5): ShelfHit[] {
  const words = wordsOf(question);
  if (words.length === 0) return [];
  const hits: ShelfHit[] = [];
  for (const doc of docs) {
    const title = doc.title.toLowerCase();
    for (const section of sectionsOf(doc, slugify)) {
      const heading = section.heading?.text.toLowerCase() ?? "";
      const body = section.text.toLowerCase();
      let score = 0;
      let matched = 0;
      for (const w of words) {
        const stem = w.length > 3 && w.endsWith("s") ? w.slice(0, -1) : w;
        const inTitle = title.includes(stem);
        const inHeading = heading.includes(stem);
        const inBody = body.includes(stem);
        if (inTitle || inHeading || inBody) matched++;
        score += (inTitle ? 3 : 0) + (inHeading ? 2 : 0) + (inBody ? 1 : 0);
      }
      if (matched < Math.ceil(words.length / 2) || score === 0 || !section.text.trim()) continue;
      score += matched * 2;
      hits.push({ slug: doc.slug, title: doc.title, ...(section.heading ? { heading: section.heading } : {}), snippet: snippetOf(section.text, words), score });
    }
  }
  // Best first; one hit per page and heading.
  const seen = new Set<string>();
  return hits
    .sort((a, b) => b.score - a.score)
    .filter((h) => {
      const key = `${h.slug}#${h.heading?.id ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}
